import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Save, Loader2, Eye, Code2 } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { useAuth } from "@/admin/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPrivacy() {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("edit"); // edit | preview

  useEffect(() => {
    fetch(`${API}/api/privacy`)
      .then((r) => r.json())
      .then((d) => {
        setContent(d.content || "");
        setUpdatedAt(d.updated_at || null);
        setUpdatedBy(d.updated_by || null);
      })
      .catch(() => toast.error("Não foi possível carregar a política."))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!content.trim()) {
      toast.error("O conteúdo não pode estar vazio.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/privacy`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Falha ao salvar.");
      }
      const data = await res.json();
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by);
      toast.success("Política de privacidade atualizada.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <AdminLayout title="Política de Privacidade">
        <p
          data-testid="privacy-admin-denied"
          className="text-sm text-[#D94A0A]"
        >
          Apenas administradores podem editar esta página.
        </p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Política de Privacidade"
      subtitle="Edite o conteúdo público de /privacidade. Aceita Markdown."
    >
      <div
        data-testid="admin-privacy-page"
        className="rounded-2xl border border-[#1B1D1D]/10 bg-white"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#1B1D1D]/8 px-5 py-3">
          <div className="flex items-center gap-1 rounded-full bg-[#F5FAFB] p-1">
            <ToggleBtn
              active={mode === "edit"}
              onClick={() => setMode("edit")}
              icon={Code2}
              label="Editar"
              testid="privacy-mode-edit"
            />
            <ToggleBtn
              active={mode === "preview"}
              onClick={() => setMode("preview")}
              icon={Eye}
              label="Preview"
              testid="privacy-mode-preview"
            />
          </div>

          <div className="flex items-center gap-3">
            {updatedAt && (
              <span
                data-testid="privacy-meta"
                className="hidden sm:inline text-xs text-[#7D8188]"
              >
                Atualizada em{" "}
                {new Date(updatedAt).toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {updatedBy ? ` por ${updatedBy}` : ""}
              </span>
            )}
            <button
              onClick={save}
              disabled={saving || loading}
              data-testid="privacy-save-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B1D1D] px-5 py-2 text-sm font-semibold text-white hover:bg-[#000] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>

        <div className="p-5">
          {loading ? (
            <p className="text-sm text-[#7D8188]">Carregando…</p>
          ) : mode === "edit" ? (
            <textarea
              data-testid="privacy-editor"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              className="w-full min-h-[60vh] rounded-xl border border-[#1B1D1D]/15 bg-white p-4 font-mono text-[13px] leading-relaxed text-[#1B1D1D] focus:border-[#208BFF] focus:outline-none focus:ring-2 focus:ring-[#208BFF]/20 transition-all"
              placeholder="# Política de Privacidade&#10;&#10;Use Markdown..."
            />
          ) : (
            <article
              data-testid="privacy-preview"
              className="rsm-prose max-w-3xl"
            >
              <ReactMarkdown>{content || "_(vazio)_"}</ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function ToggleBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-white text-[#1B1D1D] shadow-sm"
          : "text-[#7D8188] hover:text-[#1B1D1D]"
      }`}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}
